/**
 * Корзина. Живёт на клиенте: на сервер уходит только при создании заказа.
 *
 * Хранится в localStorage — переживает закрытие мини-аппа, как и ожидает
 * пользователь. Цены здесь только для отображения: итог считает сервер,
 * и сумма не входит в контракт запроса (docs/04-api.md).
 *
 * Корзина принадлежит ВИТРИНЕ. В маркетплейсе «корзины вообще» не существует:
 * товар, цена доставки и слоты принадлежат конкретному исполнителю, и смешать
 * в одном заказе арбуз одного продавца с дыней другого нельзя — заказ поедет
 * к кому-то одному. Поэтому смена исполнителя корзину очищает, и делает это
 * явно, с подтверждения человека, а не молча.
 */
import { useCallback, useEffect, useState } from 'react';
import type { PublicProduct } from '@arbuz/shared';

/**
 * Версия ключа поднята: в v1 лежали позиции без витрины, и оформить их в новой
 * модели всё равно нельзя. Старый ключ читаем один раз — чтобы удалить.
 */
const STORAGE_KEY = 'arbuz.cart.v2';
const LEGACY_KEY = 'arbuz.cart.v1';
const MAX_QTY = 50;

export interface CartLine {
  product_id: number;
  qty: number;
}

export interface CartState {
  vendor_id: number | null;
  lines: CartLine[];
}

const EMPTY: CartState = { vendor_id: null, lines: [] };

function parseLines(value: unknown): CartLine[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((v): CartLine[] => {
    if (typeof v !== 'object' || v === null) return [];
    const o = v as Record<string, unknown>;
    const id = Number(o.product_id);
    const qty = Number(o.qty);
    if (!Number.isInteger(id) || id <= 0) return [];
    if (!Number.isInteger(qty) || qty <= 0) return [];
    return [{ product_id: id, qty: Math.min(qty, MAX_QTY) }];
  });
}

function read(): CartState {
  try {
    // Корзина прошлой версии не переносится: в ней нет исполнителя, а
    // подставить его наугад — значит оформить заказ не тому.
    localStorage.removeItem(LEGACY_KEY);

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY;

    const o = parsed as Record<string, unknown>;
    const vendorId = Number(o.vendor_id);
    const lines = parseLines(o.lines);

    if (!Number.isInteger(vendorId) || vendorId <= 0) return EMPTY;
    return { vendor_id: vendorId, lines };
  } catch {
    // Повреждённое хранилище не должно ронять приложение
    return EMPTY;
  }
}

function write(state: CartState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Приватный режим или переполнение — корзина останется в памяти
  }
}

/** Простая шина, чтобы несколько экранов видели одну корзину. */
const listeners = new Set<(state: CartState) => void>();
let current: CartState = read();

function publish(next: CartState): void {
  current = next;
  write(next);
  for (const l of listeners) l(next);
}

export function useCart() {
  const [state, setState] = useState<CartState>(current);

  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  /**
   * Переключение на другую витрину. Позиции прежней всегда сбрасываются: они
   * принадлежали чужому каталогу, и сервер их всё равно не примет.
   * Спросить человека — задача экрана, а не хранилища.
   */
  const switchVendor = useCallback((vendorId: number) => {
    if (current.vendor_id === vendorId) return;
    publish({ vendor_id: vendorId, lines: [] });
  }, []);

  const setQty = useCallback((productId: number, qty: number) => {
    const clamped = Math.max(0, Math.min(qty, MAX_QTY));
    const rest = current.lines.filter((l) => l.product_id !== productId);
    publish({
      vendor_id: current.vendor_id,
      lines: clamped === 0 ? rest : [...rest, { product_id: productId, qty: clamped }],
    });
  }, []);

  const add = useCallback(
    (productId: number) => {
      const existing = current.lines.find((l) => l.product_id === productId);
      setQty(productId, (existing?.qty ?? 0) + 1);
    },
    [setQty],
  );

  const clear = useCallback(() => publish({ vendor_id: current.vendor_id, lines: [] }), []);

  const qtyOf = useCallback(
    (productId: number) => state.lines.find((l) => l.product_id === productId)?.qty ?? 0,
    [state.lines],
  );

  const count = state.lines.reduce((sum, l) => sum + l.qty, 0);

  return {
    vendorId: state.vendor_id,
    lines: state.lines,
    switchVendor,
    add,
    setQty,
    clear,
    qtyOf,
    count,
  };
}

/** Предварительный итог для отображения. Настоящий считает сервер. */
export function estimateTotal(lines: CartLine[], products: Map<number, PublicProduct>): number {
  return lines.reduce((sum, l) => {
    const p = products.get(l.product_id);
    return p ? sum + Number(p.price) * l.qty : sum;
  }, 0);
}
